import { ViewIds } from '@ardoq/api-types';
import { LayoutOrientation } from '@ardoq/global-consts';
import { pick } from 'lodash';
import { DEFAULT_VIEWPOINT_MODE_SETTINGS } from 'views/environmentals/viewSettings/defaultStreamState';
import { isViewpointModeView } from 'views/environmentals/viewSettings/utils';
import { SettableModernizedBlockDiagramViewSettings } from 'tabview/blockDiagram/types';
import { SettableComponentMatrix2ViewSettings } from 'tabview/componentMatrix/componentMatrix2/types';
import { SettableDependencyMapViewSettings } from '@ardoq/dependency-map';
import { SettableBubbleChartViewSettings } from '@ardoq/bubble-chart';
import { SettableTimelineViewSettings } from '@ardoq/timeline2024';
import { SettableRelationshipsViewSettings } from 'tabview/relationsD3View/hierarchical/types';
import { FlexibleBlockDiagramViewSettings } from 'tabview/flexibleBlockDiagram/types';
import { SettableBlocksViewSettings } from 'tabview/blocks/types';
// @yfiles/yfiles's LayoutOrientation is a numeric enum declared in an ambient
// .d.ts, so typia can't resolve its member values into the AI-facing schema.
// The AI is told to use this string form instead of the raw enum number.
const LAYOUT_ORIENTATION_AI_VALUES = [
    'top-to-bottom',
    'left-to-right',
    'right-to-left',
    'bottom-to-top',
] as const;
export type LayoutOrientationAiValue = (typeof LAYOUT_ORIENTATION_AI_VALUES)[number];
const LAYOUT_ORIENTATION_BY_AI_VALUE: Record<LayoutOrientationAiValue, LayoutOrientation> = {
    'top-to-bottom': LayoutOrientation.TOP_TO_BOTTOM,
    'left-to-right': LayoutOrientation.LEFT_TO_RIGHT,
    'right-to-left': LayoutOrientation.RIGHT_TO_LEFT,
    'bottom-to-top': LayoutOrientation.BOTTOM_TO_TOP,
};
const isLayoutOrientationAiValue = (value: unknown): value is LayoutOrientationAiValue => LAYOUT_ORIENTATION_AI_VALUES.some(aiValue => aiValue === value);
/** `null` for anything the AI supplies that is not one of the four values. */
export const toLayoutOrientation = (value: unknown): LayoutOrientation | null => isLayoutOrientationAiValue(value)
    ? LAYOUT_ORIENTATION_BY_AI_VALUE[value]
    : null;
export const toLayoutOrientationAiValue = (layoutOrientation: unknown): LayoutOrientationAiValue | undefined => LAYOUT_ORIENTATION_AI_VALUES.find(aiValue => LAYOUT_ORIENTATION_BY_AI_VALUE[aiValue] === layoutOrientation);
export const LAYOUT_SETTINGS_BY_VIEW_ID = new Map<ViewIds, string[]>([
    [
        ViewIds.MODERNIZED_BLOCK_DIAGRAM,
        [
            'layoutOrientation',
            'separateReferences',
            'componentStyle',
            'showOnlyConnectedComponents',
        ],
    ],
    [
        ViewIds.RELATIONSHIPS_3,
        ['bundleRelationships', 'showOnlyConnectedComponents'],
    ],
    [
        ViewIds.DEPENDENCY_MAP_2,
        ['showVertical', 'colorByReference', 'showOnlyConnectedComponents'],
    ],
    [
        ViewIds.TIMELINE,
        [
            'domainRange',
            'gridPrecision',
            'selectedPhaseFields',
            'selectedMilestoneFields',
            'persistedCollapsedState',
        ],
    ],
    [
        ViewIds.BUBBLE,
        [
            'selectedFieldNameX',
            'selectedFieldNameY',
            'selectedFieldNameRadius',
            'background',
            'customQuadrantSettings',
            'bubbleAreaScaleFactor',
            'showBubblesWithZeroValue',
            'selectedComponentTypes',
            'selectedQuadrant',
            'fieldSource',
            'selectedReferenceTypeName',
            'selectedReferenceDirection',
            'valueSourceComponentTypes',
        ],
    ],
    [
        ViewIds.BLOCKS,
        ['linksAggregation', 'componentStyle', 'layoutData', 'arrangements'],
    ],
    /* The whole configuration of this view is three field picks plus how the surface between them
       is built, so a viewpoint that stored none of it opens on whatever the reader's own settings
       slice happens to hold - blank for a first-time reader, someone else's last three fields for
       anyone who has used the view before. `ramp` and `bands` have no control yet and are listed
       anyway: they shape the picture, and a setting a viewpoint does not supply is exactly what
       leaks from the previously open one. */
    [
        ViewIds.SURFACE_3D,
        [
            'selectedFieldNameX',
            'selectedFieldNameY',
            'selectedFieldNameZ',
            'interpolation',
            'aggregation',
            'resolution',
            'ramp',
            'bands',
            'contourCount',
            'showWireframe',
            'showDrapedGrid',
            'showOutlines',
            'showDropLines',
            'showSamplePositions',
        ],
    ],
    /* The same argument as the surface above, which these three share the field pickers with. The
       contour keeps the whole gridding vocabulary and swaps the surface's camera-side toggles for its
       own; the two point charts grid nothing, so none of it appears. */
    [
        ViewIds.CONTOUR,
        [
            'selectedFieldNameX',
            'selectedFieldNameY',
            'selectedFieldNameZ',
            'interpolation',
            'aggregation',
            'resolution',
            'ramp',
            'bands',
            'contourCount',
            'showFilledBands',
            'showIsolines',
            'showSamplePositions',
        ],
    ],
    /* One list for both point charts, because they persist one settings shape - which is also what
       makes the omission worse here than on the surface: a shared 3D Bubble viewpoint that stored
       nothing opened on whatever the reader last chose on the 3D Scatter. */
    ...([ViewIds.SCATTER_3D, ViewIds.BUBBLE_3D] as const).map((viewId): [
        ViewIds,
        string[]
    ] => [
        viewId,
        [
            'selectedFieldNameX',
            'selectedFieldNameY',
            'selectedFieldNameZ',
            'selectedFieldNameRadius',
            'ramp',
            'bands',
        ],
    ]),
    /* The layout and what size stands for. The other dials the package exposes are not offered in
       the settings bar, so a viewpoint has no way to have changed them - persisting those would
       store a value no reader chose and no control can reach. */
    [
        ViewIds.NETWORK_3D,
        [
            'layoutKind',
            'nodeSizeBy',
            'sizeByFieldName',
            'collapsedGroupIds',
            'showLabels',
        ],
    ],
    [ViewIds.SPIDER_2, ['selectedFieldNames', 'markType']],
    [
        ViewIds.POLAR,
        [
            'selectedFieldNameAngle',
            'selectedFieldNameRadius',
            'selectedFieldNameSeries',
            'markType',
        ],
    ],
    [
        ViewIds.FLEXIBLE_BLOCK_DIAGRAM,
        [
            'layoutOrientation',
            'separateReferences',
            'componentStyle',
            'layoutType',
            'hideReferences',
        ],
    ],
    /* One saved set per view, now that each has its own settings type. The three used to share
       one, so all three carried `tilingMethod` — a viewpoint saved from the circle packing or the
       sunburst stored a tiling nothing reads. Only the treemap tiles, and only its list says so. */
    [
        ViewIds.HIERARCHICAL_TREEMAP,
        ['sizeByFieldName', 'tilingMethod', 'maxDepth'],
    ],
    [ViewIds.HIERARCHICAL_PACKING, ['sizeByFieldName', 'maxDepth']],
    [ViewIds.SUNBURST, ['sizeByFieldName', 'maxDepth']],
    [
        ViewIds.COMPONENT_MATRIX_2,
        [
            'selectedFieldNamesX',
            'selectedFieldNamesY',
            'useParentAsX',
            'useParentAsY',
            'referenceTypeX',
            'referenceTypeY',
            'selectedComponentTypes',
            'showEmptyValues',
            'sortOrderX',
            'sortOrderY',
            'displayTextInViewX',
            'displayTextInViewY',
            'additionalHeaderFieldNamesX',
            'additionalHeaderFieldNamesY',
            'colorRowColumnHeaderByComponent',
        ],
    ],
    [
        ViewIds.ASSISTANT_BLOCKS,
        [
            'arrangements',
            'title',
            'collapsedGroupIds',
            'separateReferences',
            'hideReferences',
        ],
    ],
]);
export const MODERNIZED_BLOCK_DIAGRAM_AI_CONTEXT_SETTING_KEYS = [
    'isLegendActive',
    'collapsedGroupIds',
    'layoutOrientation',
    'separateReferences',
    'componentStyle',
] as const satisfies readonly (keyof SettableModernizedBlockDiagramViewSettings)[];
export const COMPONENT_MATRIX_2_AI_CONTEXT_SETTING_KEYS = [
    'componentTypes',
    'xAxis',
    'yAxis',
    'showEmptyValues',
    'colorHeadersByComponent',
    'isLegendActive',
] as const satisfies readonly (keyof SettableComponentMatrix2ViewSettings)[];
export const DEPENDENCY_MAP_AI_CONTEXT_SETTING_KEYS = [
    'isLegendActive',
    'colorByReference',
    'degreesOfParenthood',
    'showVertical',
    'treeDepth',
] as const satisfies readonly (keyof SettableDependencyMapViewSettings)[];
export const BUBBLE_AI_CONTEXT_SETTING_KEYS = [
    'selectedComponentTypes',
    'selectedFieldNameX',
    'selectedFieldNameY',
    'selectedFieldNameRadius',
    'background',
    'customQuadrantSettings',
    'bubbleAreaScaleFactor',
    'showBubblesWithZeroValue',
    'selectedQuadrant',
    'labelFilter',
    'isLegendActive',
] as const satisfies readonly (keyof SettableBubbleChartViewSettings)[];
export const RELATIONSHIPS_AI_CONTEXT_SETTING_KEYS = [
    'isLegendActive',
    'collapsedGroupIds',
    'bundleRelationships',
] as const satisfies readonly (keyof SettableRelationshipsViewSettings)[];
export const TIMELINE_AI_CONTEXT_SETTING_KEYS = [
    'selectedPhaseFields',
    'selectedMilestoneFields',
    'gridPrecision',
    'domainRange',
    'persistedCollapsedState',
    'isLegendActive',
] as const satisfies readonly (keyof SettableTimelineViewSettings)[];
export const FLEXIBLE_BLOCK_DIAGRAM_AI_CONTEXT_SETTING_KEYS = [
    'isLegendActive',
    'collapsedGroupIds',
    'layoutOrientation',
    'separateReferences',
    'componentStyle',
    'hideReferences',
    'layoutType',
] as const satisfies readonly (keyof FlexibleBlockDiagramViewSettings)[];
export const BLOCKS_AI_CONTEXT_SETTING_KEYS = [
    'linksAggregation',
    'componentStyle',
    'isLegendActive',
] as const satisfies readonly (keyof SettableBlocksViewSettings)[];
export const PERSISTED_VIEW_ACTIONS_SETTINGS_BY_VIEW_ID = new Map<ViewIds, string[]>([
    [
        ViewIds.DEPENDENCY_MAP_2,
        ['degreesOfParenthood', 'degreesOfChildhood', 'treeDepth'],
    ],
]);
const listedFor = (viewId: ViewIds): string[] => [
    ...(LAYOUT_SETTINGS_BY_VIEW_ID.get(viewId) ?? []),
    ...(PERSISTED_VIEW_ACTIONS_SETTINGS_BY_VIEW_ID.get(viewId) ?? []),
];
/**
 * A viewpoint stored under one of these renders as the other view, so it owns both
 * views' settings. Paired statically rather than read off the feature flag: with the
 * swap off the other view's names are in neither the settings being saved nor the
 * defaults being filled, so listing them costs nothing.
 *
 * This makes the *key set* flag-independent, not the save path as a whole: the values
 * come from the rendered view's own settings slice, so resaving a viewpoint with the
 * swap off still drops the names that view does not have — the other one's.
 */
const SWAPPED_WITH = new Map<ViewIds, ViewIds>([
    [ViewIds.MODERNIZED_BLOCK_DIAGRAM, ViewIds.FLEXIBLE_BLOCK_DIAGRAM],
    [ViewIds.FLEXIBLE_BLOCK_DIAGRAM, ViewIds.MODERNIZED_BLOCK_DIAGRAM],
]);
/**
 * The settings a viewpoint stored under `viewId` owns. Saving and applying both go
 * through here, so a name one side knows and the other doesn't — never reset, or reset
 * on every open — can't arise. Applying unions this with the rendered view's answer;
 * saving doesn't need to, because the id it stores is never one of the collapsed ones.
 */
export const savedWithViewpoint = (viewId: ViewIds): string[] => {
    const swappedWith = SWAPPED_WITH.get(viewId);
    return [
        ...new Set([
            ...listedFor(viewId),
            ...(swappedWith ? listedFor(swappedWith) : []),
        ]),
    ];
};
type OmittedSettingsArgs = {
    /** The view the viewpoint was saved from. Contributes to the key set. */
    storedViewId: ViewIds;
    /** The view being rendered: contributes to the key set and supplies the values. */
    renderedViewId: ViewIds;
    storedSettings: Record<string, unknown>;
};
/**
 * The stored settings, with a default for every setting the viewpoint owns but
 * did not store. Applying is a partial merge, so an omitted setting would
 * otherwise keep whatever the previously open viewpoint left in the stream — and
 * a setting is omitted whenever its author never changed it from a default
 * `JSON.stringify` drops, or saved the viewpoint before the setting was one the
 * viewpoint owns.
 *
 * The two view ids differ for two reasons: the swap reroutes one to the other, and an
 * id viewpoint mode does not currently offer collapses to `MODERNIZED_BLOCK_DIAGRAM`,
 * which is a runtime answer rather than a fixed list. So both views' key sets apply, and
 * `pick` is the filter: a name the rendered view has no default for is dropped, so
 * unioning them can only add names that view owns.
 */
export const withDefaultsForOmittedSettings = ({ storedViewId, renderedViewId, storedSettings, }: OmittedSettingsArgs): Record<string, unknown> => {
    // Some callers pass a slice holding nothing but a transaction-id symbol, which
    // `Object.keys` does not see. That is "no settings at hand" rather than a
    // viewpoint that stored none, and filling defaults there would reset the
    // user's own settings with no viewpoint involved.
    if (Object.keys(storedSettings).length === 0) {
        return storedSettings;
    }
    const defaults = isViewpointModeView(renderedViewId)
        ? DEFAULT_VIEWPOINT_MODE_SETTINGS[renderedViewId]
        : {};
    return {
        ...pick(defaults, [
            ...new Set([
                ...savedWithViewpoint(storedViewId),
                ...savedWithViewpoint(renderedViewId),
            ]),
        ]),
        ...storedSettings,
    };
};
